import { httpCoreService } from "../services/HttpCoreService";
import type {
  Milestone,
  CreateMilestoneRequest,
  UpdateMilestoneRequest,
  MilestoneFilters,
  MilestoneListResponse,
} from "@/pages/StudyMilestones/types/milestone.type";
import type { ApiResponse } from "../types/auth.type";

interface RawApiMilestone {
  id?: string;
  milestoneName?: string;
  milestone_name?: string;
  studyId?: string;
  study_id?: string;
  studyName?: string;
  study_name?: string;
  plannedDate?: string;
  planned_date?: string;
  actualDate?: string | null;
  actual_date?: string | null;
  variance?: number;
  status?: Milestone["status"];
  description?: string;
  notes?: string;
  ownerId?: string;
  owner_id?: string;
  ownerName?: string;
  owner_name?: string;
  isArchived?: boolean;
  is_archived?: boolean;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  createdBy?: string;
  created_by?: string;
  updatedBy?: string;
  updated_by?: string;
  createdByName?: string;
  created_by_name?: string;
  updatedByName?: string;
  updated_by_name?: string;
  completedTasks?: number;
  completed_tasks?: number;
  pendingTasks?: number;
  pending_tasks?: number;
  inProgressTasks?: number;
  in_progress_tasks?: number;
  totalTasks?: number;
  total_tasks?: number;
}

interface MilestoneListApiData {
  milestones: unknown[];
  total?: number;
  page?: number;
  limit?: number;
}

export interface IMilestoneRepository {
  getMilestones(
    filters?: MilestoneFilters,
    page?: number,
    limit?: number,
  ): Promise<MilestoneListResponse>;
  getMilestoneById(id: string): Promise<Milestone>;
  createMilestone(milestone: CreateMilestoneRequest): Promise<Milestone>;
  updateMilestone(
    id: string,
    updates: UpdateMilestoneRequest,
  ): Promise<Milestone>;
  deleteMilestone(id: string): Promise<void>;
}
export class MilestoneRepository implements IMilestoneRepository {
  private mapApiMilestone(raw: unknown): Milestone {
    const m = raw as RawApiMilestone;
    return {
      id: m.id ?? "",
      milestoneName: m.milestoneName ?? m.milestone_name ?? "",
      studyId: m.studyId ?? m.study_id ?? "",
      studyName: m.studyName ?? m.study_name ?? "",
      plannedDate: m.plannedDate ?? m.planned_date ?? "",
      actualDate: m.actualDate ?? m.actual_date ?? null,
      variance: m.variance ?? 0,
      status: m.status ?? "Planned",
      description: m.description ?? undefined,
      notes: m.notes ?? undefined,
      ownerId: m.ownerId ?? m.owner_id ?? undefined,
      ownerName: m.ownerName ?? m.owner_name ?? undefined,
      isArchived: m.isArchived ?? m.is_archived ?? false,
      createdAt: m.createdAt ?? m.created_at ?? "",
      updatedAt: m.updatedAt ?? m.updated_at ?? "",
      createdBy: m.createdBy ?? m.created_by ?? undefined,
      updatedBy: m.updatedBy ?? m.updated_by ?? undefined,
      createdByName: m.createdByName ?? m.created_by_name ?? undefined,
      updatedByName: m.updatedByName ?? m.updated_by_name ?? undefined,
      completedTasks: m.completedTasks ?? m.completed_tasks ?? 0,
      pendingTasks: m.pendingTasks ?? m.pending_tasks ?? 0,
      inProgressTasks: m.inProgressTasks ?? m.in_progress_tasks ?? 0,
      totalTasks: m.totalTasks ?? m.total_tasks ?? 0,
    };
  }
  async getMilestones(
    filters?: MilestoneFilters,
    page = 1,
    limit = 10,
  ): Promise<MilestoneListResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append("page", page.toString());
    queryParams.append("limit", limit.toString());
    if (filters) {
      if (filters.studyId) {
        queryParams.append("study_id", filters.studyId);
      }
      if (filters.status) {
        queryParams.append("status", filters.status);
      }
      if (filters.dateFrom) {
        queryParams.append("date_from", filters.dateFrom);
      }
      if (filters.dateTo) {
        queryParams.append("date_to", filters.dateTo);
      }
      // Add show_archived parameter (defaults to false)
      queryParams.append(
        "show_archived",
        (filters.showArchived ?? false).toString(),
      );
    } else {
      // Default to not showing archived if no filters provided
      queryParams.append("show_archived", "false");
    }
    const response: ApiResponse<MilestoneListApiData> =
      await httpCoreService.get<MilestoneListApiData>(
        `/milestones?${queryParams.toString()}`,
      );
    if (!response.data) {
      throw new Error("Failed to fetch milestones");
    }
    // API returns { milestones: [...], total, page, limit }
    const milestoneData = response.data;
    if (!milestoneData || !Array.isArray(milestoneData.milestones)) {
      throw new Error("Invalid milestone response structure");
    }
    return {
      milestones: milestoneData.milestones.map((m: unknown) =>
        this.mapApiMilestone(m),
      ),
      total: milestoneData.total ?? 0,
      page: milestoneData.page ?? page,
      limit: milestoneData.limit ?? limit,
    };
  }
  async getMilestoneById(id: string): Promise<Milestone> {
    const response: ApiResponse<Milestone> =
      await httpCoreService.get<Milestone>(`/milestones/${id}`);
    if (!response.data) {
      throw new Error(`Milestone with ID ${id} not found`);
    }
    return this.mapApiMilestone(response.data);
  }
  async createMilestone(milestone: CreateMilestoneRequest): Promise<Milestone> {
    // Build request payload with snake_case field names for API
    const payload: Record<string, string> = {
      milestone_name: milestone.milestoneName,
      study_id: milestone.studyId,
      planned_date: milestone.plannedDate,
    };
    // Add optional fields only if they have values
    if (milestone.actualDate !== undefined && milestone.actualDate !== null) {
      payload.actual_date = milestone.actualDate;
    }
    if (milestone.status) {
      payload.status = milestone.status;
    }
    if (milestone.description) {
      payload.description = milestone.description;
    }
    if (milestone.notes) {
      payload.notes = milestone.notes;
    }
    if (milestone.ownerId) {
      payload.owner_id = milestone.ownerId;
    }
    const response: ApiResponse<Milestone> = await httpCoreService.post<
      Milestone,
      typeof payload
    >("/milestones", payload);
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to create milestone",
      );
    }
    if (!response.data) {
      throw new Error(
        "Milestone creation succeeded but no data was returned from the server.",
      );
    }
    return this.mapApiMilestone(response.data);
  }
  async updateMilestone(
    id: string,
    updates: UpdateMilestoneRequest,
  ): Promise<Milestone> {
    // Convert camelCase to snake_case for API
    const payload: Record<string, string | null> = {};
    if (updates.milestoneName !== undefined) {
      payload.milestone_name = updates.milestoneName;
    }
    if (updates.plannedDate !== undefined) {
      payload.planned_date = updates.plannedDate;
    }
    if (updates.actualDate !== undefined) {
      payload.actual_date = updates.actualDate;
    }
    if (updates.status !== undefined) {
      payload.status = updates.status;
    }
    if (updates.description !== undefined) {
      payload.description = updates.description;
    }
    if (updates.notes !== undefined) {
      payload.notes = updates.notes;
    }
    if (updates.ownerId !== undefined) {
      payload.owner_id = updates.ownerId;
    }
    const response: ApiResponse<Milestone> = await httpCoreService.patch<
      Milestone,
      typeof payload
    >(`/milestones/${id}`, payload);
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to update milestone",
      );
    }
    if (!response.data) {
      throw new Error(
        "Milestone update succeeded but no data was returned from the server.",
      );
    }
    return this.mapApiMilestone(response.data);
  }
  async deleteMilestone(id: string): Promise<void> {
    const response: ApiResponse<null> = await httpCoreService.delete<null>(
      `/milestones/${id}`,
    );
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to delete milestone",
      );
    }
  }
}
